/* =============================================================================
   MAIN — ESM-Entry-Point für Grill & Green
   =============================================================================
   Lädt alle Module in einer kontrollierten Reihenfolge:
     1. utils  — helpers, dom
     2. core   — capacity, reservation-engine, audit, email-templates, state, exports
     3. views  — customer-form, customer-cancel, view-switch, admin-login
     4. views/admin — settings, audit-log, menu-editor, dashboard, stats,
                      reservations, occupancy, layout-editor, daily-sheet
     5. modals — assignment-picker, walkin
     6. realtime
     7. bootstrap

   Jedes Modul exportiert seine Funktionen via `export` UND hängt sie ans
   `window`-Objekt, damit inline-onclick-Handler aus index.html und den
   gerenderten HTML-Strings unverändert funktionieren.

   Am Ende: bootstrap() starten.
============================================================================ */

// Utils
import * as helpers from './utils/helpers.js';
import * as dom from './utils/dom.js';

// Core
import * as capacity from './core/capacity.js';
import * as engine from './core/reservation-engine.js';
import * as audit from './core/audit.js';
import * as emailTemplates from './core/email-templates.js';
import * as appState from './core/state.js';
import * as exports_ from './core/exports.js';
import * as tseAdapter from './core/tse-adapter.js';
import * as printerAdapter from './core/printer-adapter.js';
import * as displayConfig from './core/display-config.js';
import * as orderStatuses from './core/order-statuses.js';
// S59 Block B + C — Service-Channel + Source-Channel + Delivery-Routing.
// Module setzen window.ServiceChannels / window.OrderSources / window.DeliveryRouting
// als Side-Effect (analog order-statuses.js) für inline-onclick + storage.js.
import * as serviceChannels from './core/service-channels.js';
import * as orderSources from './core/order-sources.js';
import * as deliveryRouting from './core/delivery-routing.js';
// S59 Block H — Cross-Cutting Pagination-Helper.
// Setzt window.PaginatedList + window.ExpandList für inline-onclick.
import * as paginatedList from './core/paginated-list.js';
import * as liveViewRegistry from './core/live-view-registry.js';
import * as domBindings from './core/dom-bindings.js';
import * as settingHelpers from './core/setting-helpers.js';

// Views
import * as customerCancel from './views/customer-cancel.js';
import * as customerStatus from './views/customer-status.js';
import * as customerNewsletter from './views/customer-newsletter.js';
// S244 Cut B — Gast-Seite am Tisch (#tisch/<token>). Import registriert die
// window-Bindings fuer den bootstrap-Dispatcher + die Inline-Handler.
import * as customerTable from './views/customer-table.js';
import * as customerTableCart from './views/customer-table-cart.js';
// S262 Cut A — Mitarbeiter-Seite Dienstplan (#dienstplan/<token>). Import
// registriert die window-Bindings fuer den bootstrap-Dispatcher + Inline-Handler.
import * as customerSchedule from './views/customer-schedule.js';
// S263 Cut B — Manager-Maske Dienstplan (Topbar-Reiter, #admin/schedule/plan).
// Import registriert window.renderSchedulePlan + die sp*-Inline-Handler.
import * as schedulePlan from './views/admin/schedule-plan.js';
import * as customerForm from './views/customer-form.js';
import * as menuPicker from './views/menu-picker.js';
// S69 REF-4: menu-picker.js in drei Sub-Module zerlegt — Side-Effect-Import
// reicht (Sub-Module exportieren reine Funktionen, keine window-Bindings;
// die Inline-onclick-Handler greifen über die in menu-picker.js gepflegten
// `Object.assign(window, {...})`-Bindings, die ihrerseits die mp*-Funktionen
// aus menu-picker-actions.js re-importieren).
import * as menuPickerHelpers from './views/menu-picker-helpers.js';
import * as menuPickerRenderLine from './views/menu-picker-render-line.js';
import * as menuPickerActions from './views/menu-picker-actions.js';
import * as takeawayForm from './views/takeaway-form.js';
import * as deliveryForm from './views/delivery-form.js';
import * as viewSwitch from './views/view-switch.js';
import * as adminLogin from './views/admin-login.js';

// Views / Admin
import * as adminShell from './views/admin/shell.js';
import * as adminSettings from './views/admin/settings.js';
import * as adminSettingsPrinters from './views/admin/settings-printers.js';
// S66 Refactor: Settings-Subtab "Displays" als eigenes Modul.
import * as adminSettingsDisplays from './views/admin/settings-displays.js';
// S66 Refactor: Settings-Subtab "Delivery-Rules" als eigenes Modul.
import * as adminSettingsDeliveryRules from './views/admin/settings-delivery-rules.js';
// S66 Refactor: Settings-Subtab "Compliance/TSE" als eigenes Modul.
import * as adminSettingsComplianceTse from './views/admin/settings-compliance-tse.js';
// S66 Refactor: Settings-Subtab "Team" als eigenes Modul.
import * as adminSettingsTeam from './views/admin/settings-team.js';
import * as adminMenuEditor from './views/admin/menu-editor.js';
import * as adminDealsAdmin from './views/admin/deals-admin.js';            // S169 P2 Deals-Tab
import * as adminDiscountsAdmin from './views/admin/discounts-admin.js';    // S179 R2 Rabatte-Tab
import * as adminIngredients from './views/admin/ingredients-admin.js';     // S151 ERP A1
import * as adminMenuRecipeEditor from './views/admin/menu-recipe-editor.js'; // S151 ERP A1
import * as adminNutritionCache from './views/admin/nutrition-cache.js';      // S152 ERP A2
import * as uiDialogs from './views/ui/dialogs.js';                            // S190 styled Confirm/Prompt (kein native prompt/confirm)
import * as adminMenuCategories from './views/admin/menu-categories-admin.js'; // S153 ERP Rubriken
import * as adminOptionGroups from './views/admin/option-groups-admin.js';     // S188-2 Optionssätze-Editor
import * as adminMenuConfigurator from './views/admin/menu-configurator.js';  // S155 ERP D1 Konfigurator
import * as adminFoodCostReport from './views/admin/food-cost-report.js';     // S158 ERP B Food-Cost
import * as adminStockAdmin from './views/admin/stock-admin.js';              // E1 ERP E Lager
import * as adminSuppliersAdmin from './views/admin/suppliers-admin.js';      // S163 ERP E4a Lieferanten
import * as adminPurchaseOrdersAdmin from './views/admin/purchase-orders-admin.js'; // S163 ERP E4b Bestellwesen
import * as adminShrinkageAdmin from './views/admin/shrinkage-admin.js';           // S164 ERP E5 Schwund
import * as adminLotsAdmin from './views/admin/lots-admin.js';                     // S165 ERP E6a Chargen/MHD
import * as adminDashboard from './views/admin/dashboard.js';
import * as adminStats from './views/admin/stats.js';
import * as adminReservations from './views/admin/reservations.js';
// S69 REF-3: reservations.js in fünf Sub-Module zerlegt — Sub-Module müssen
// importiert werden, damit ihre Object.assign(window, ...) Bindings für
// Inline-Handler beim Boot ausgeführt werden.
import * as adminReservationsHelpers from './views/admin/reservations-helpers.js';
import * as adminReservationsCustomers from './views/admin/reservations-customers.js';
import * as adminReservationsConfirm from './views/admin/reservations-confirm.js';
import * as adminReservationsActions from './views/admin/reservations-actions.js';
// S239 Cut A0 — „✅ Gäste sind da" (Check-in-Aktion + window-Bindings).
import * as adminReservationCheckin from './views/admin/reservation-checkin.js';
import * as adminOrders from './views/admin/orders.js';
// S67 Refactor: orders.js in drei Module zerlegt — Sub-Module müssen importiert
// werden, damit ihre Object.assign(window, ...) Bindings für Inline-Handler
// beim Boot ausgeführt werden.
import * as adminOrdersModalState from './views/admin/orders-modal-state.js';
import * as adminOrdersCorrection from './views/admin/orders-correction.js';
import * as adminOrdersReceiptActions from './views/admin/orders-receipt-actions.js';
import * as adminOrdersSplit from './views/admin/orders-split.js'; // S135 P4 (2b) — Split nach Positionen
import * as adminOrdersNotify from './views/admin/orders-notifications.js';
import * as adminOrderProcessHistory from './views/admin/order-process-history.js'; // S150 — Prozessübersicht
import * as adminDayClose from './views/admin/day-close.js';
import * as adminDayCloseState from './views/admin/day-close-state.js';
import * as adminDayCloseRender from './views/admin/day-close-render.js';
import * as adminDayCloseZbonAction from './views/admin/day-close-zbon-action.js';
import * as adminDayCloseArchiveDetail from './views/admin/day-close-archive-detail.js';
import * as adminReceipt from './views/admin/receipt.js';
import * as datevExport from './utils/datev-export.js';
import * as dsfinvkExport from './utils/dsfinvk-export.js';
import * as adminOccupancy from './views/admin/occupancy.js';
// S245 Cut C — Tisch-Signale (Band + Ack), geteilt von Belegung und Bestellungen.
import * as adminTableSignals from './views/admin/table-signals.js';
import * as adminTableCart from './views/admin/table-cart.js';
import * as adminLayout from './views/admin/layout-editor.js';
import * as adminDailySheet from './views/admin/daily-sheet.js';
import * as adminKds from './views/admin/kds.js';
import * as adminBar from './views/admin/bar.js';
import * as adminDelivery from './views/admin/delivery.js';
import * as driverTerminal from './views/driver-terminal.js';  // S209 — Fahrer-Terminal
import * as adminArchive from './views/admin/archive.js';
import * as adminArchiveState from './views/admin/archive-state.js';
import * as adminArchiveHelpers from './views/admin/archive-helpers.js';
import * as adminArchiveReservations from './views/admin/archive-reservations.js';
import * as adminArchiveOrders from './views/admin/archive-orders.js';
import * as adminArchiveBons from './views/admin/archive-bons.js';
import * as adminArchiveZbons from './views/admin/archive-zbons.js';
import * as adminArchiveMails from './views/admin/archive-mails.js';
import * as adminOrderEntry from './views/admin/admin-order-entry.js';
import * as adminOrderEntryState from './views/admin/admin-order-entry-state.js';
import * as adminOrderEntryDelivery from './views/admin/admin-order-entry-delivery.js';
import * as adminOrderEntryDinein from './views/admin/admin-order-entry-dinein.js';
import * as adminOrderEntrySubmit from './views/admin/admin-order-entry-submit.js';

// Modals
import * as assignmentPicker from './views/assignment-picker.js';
import * as walkin from './views/walkin.js';

// Realtime + Bootstrap
import * as realtime from './realtime.js';
import { bootstrap } from './bootstrap.js';

// Debug-Log: zeigt jede Modul-Liste, damit „wo ist was geladen" auf einen Blick sichtbar bleibt.
// Audit 2026-05-20 (L-10): nur im Dev-Build geloggt, in Production fällt das weg —
// Vite ersetzt import.meta.env.DEV beim Build durch true/false und tree-shaked
// den toten Branch weg.
if (import.meta.env.DEV) {
  console.info('[main.js] ESM-Entry geladen.',
  '\n  utils/helpers:                ', Object.keys(helpers).join(', '),
  '\n  utils/dom:                    ', Object.keys(dom).join(', '),
  '\n  core/capacity:                ', Object.keys(capacity).join(', '),
  '\n  core/reservation-engine:      ', Object.keys(engine).join(', '),
  '\n  core/audit:                   ', Object.keys(audit).join(', '),
  '\n  core/email-templates:         ', Object.keys(emailTemplates).join(', '),
  '\n  core/state:                   ', Object.keys(appState).join(', '),
  '\n  core/exports:                 ', Object.keys(exports_).join(', '),
  '\n  core/tse-adapter:             ', Object.keys(tseAdapter).join(', '),
  '\n  core/printer-adapter:         ', Object.keys(printerAdapter).join(', '),
  '\n  core/display-config:          ', Object.keys(displayConfig).join(', '),
  '\n  core/order-statuses:          ', Object.keys(orderStatuses).join(', '),
  '\n  core/service-channels:        ', Object.keys(serviceChannels).join(', '),
  '\n  core/order-sources:           ', Object.keys(orderSources).join(', '),
  '\n  core/delivery-routing:        ', Object.keys(deliveryRouting).join(', '),
  '\n  core/paginated-list:          ', Object.keys(paginatedList).join(', '),
  '\n  core/live-view-registry:      ', Object.keys(liveViewRegistry).join(', '),
  '\n  core/dom-bindings:            ', Object.keys(domBindings).join(', '),
  '\n  core/setting-helpers:         ', Object.keys(settingHelpers).join(', '),
  '\n  views/customer-cancel:        ', Object.keys(customerCancel).join(', '),
  '\n  views/customer-status:        ', Object.keys(customerStatus).join(', '),
  '\n  views/customer-newsletter:    ', Object.keys(customerNewsletter).join(', '),
  '\n  views/customer-form:          ', Object.keys(customerForm).join(', '),
  '\n  views/menu-picker:            ', Object.keys(menuPicker).join(', '),
  '\n  views/menu-picker-helpers:    ', Object.keys(menuPickerHelpers).join(', '),
  '\n  views/menu-picker-render-line:', Object.keys(menuPickerRenderLine).join(', '),
  '\n  views/menu-picker-actions:    ', Object.keys(menuPickerActions).join(', '),
  '\n  views/takeaway-form:          ', Object.keys(takeawayForm).join(', '),
  '\n  views/customer-table:         ', Object.keys(customerTable).join(', '),
  '\n  views/customer-table-cart:    ', Object.keys(customerTableCart).join(', '),
  '\n  views/customer-schedule:      ', Object.keys(customerSchedule).join(', '),
  '\n  views/view-switch:            ', Object.keys(viewSwitch).join(', '),
  '\n  views/admin-login:            ', Object.keys(adminLogin).join(', '),
  '\n  views/admin/shell:            ', Object.keys(adminShell).join(', '),
  '\n  views/admin/schedule-plan:    ', Object.keys(schedulePlan).join(', '),
  '\n  views/admin/settings:         ', Object.keys(adminSettings).join(', '),
  '\n  views/admin/settings-printers:', Object.keys(adminSettingsPrinters).join(', '),
  '\n  views/admin/settings-displays:', Object.keys(adminSettingsDisplays).join(', '),
  '\n  views/admin/settings-delivery-rules:', Object.keys(adminSettingsDeliveryRules).join(', '),
  '\n  views/admin/settings-compliance-tse:', Object.keys(adminSettingsComplianceTse).join(', '),
  '\n  views/admin/settings-team:', Object.keys(adminSettingsTeam).join(', '),
  '\n  views/admin/order-process-history:', Object.keys(adminOrderProcessHistory).join(', '),
  '\n  views/admin/menu-editor:      ', Object.keys(adminMenuEditor).join(', '),
  '\n  views/admin/deals-admin:      ', Object.keys(adminDealsAdmin).join(', '),
  '\n  views/admin/discounts-admin:  ', Object.keys(adminDiscountsAdmin).join(', '),
  '\n  views/admin/ingredients-admin:', Object.keys(adminIngredients).join(', '),
  '\n  views/admin/menu-recipe-editor:', Object.keys(adminMenuRecipeEditor).join(', '),
  '\n  views/admin/nutrition-cache:', Object.keys(adminNutritionCache).join(', '),
  '\n  views/ui/dialogs:', Object.keys(uiDialogs).join(', '),
  '\n  views/admin/menu-categories-admin:', Object.keys(adminMenuCategories).join(', '),
  '\n  views/admin/option-groups-admin:', Object.keys(adminOptionGroups).join(', '),
  '\n  views/admin/menu-configurator:', Object.keys(adminMenuConfigurator).join(', '),
  '\n  views/admin/food-cost-report:', Object.keys(adminFoodCostReport).join(', '),
  '\n  views/admin/stock-admin:', Object.keys(adminStockAdmin).join(', '),
  '\n  views/admin/suppliers-admin:', Object.keys(adminSuppliersAdmin).join(', '),
  '\n  views/admin/purchase-orders-admin:', Object.keys(adminPurchaseOrdersAdmin).join(', '),
  '\n  views/admin/shrinkage-admin:', Object.keys(adminShrinkageAdmin).join(', '),
  '\n  views/admin/lots-admin:', Object.keys(adminLotsAdmin).join(', '),
  '\n  views/admin/dashboard:        ', Object.keys(adminDashboard).join(', '),
  '\n  views/admin/stats:            ', Object.keys(adminStats).join(', '),
  '\n  views/admin/reservations:     ', Object.keys(adminReservations).join(', '),
  '\n  views/admin/reservations-helpers:  ', Object.keys(adminReservationsHelpers).join(', '),
  '\n  views/admin/reservations-customers:', Object.keys(adminReservationsCustomers).join(', '),
  '\n  views/admin/reservations-confirm:  ', Object.keys(adminReservationsConfirm).join(', '),
  '\n  views/admin/reservations-actions:  ', Object.keys(adminReservationsActions).join(', '),
  '\n  views/admin/reservation-checkin:   ', Object.keys(adminReservationCheckin).join(', '),
  '\n  views/admin/orders:           ', Object.keys(adminOrders).join(', '),
  '\n  views/admin/orders-modal-state:', Object.keys(adminOrdersModalState).join(', '),
  '\n  views/admin/orders-correction:', Object.keys(adminOrdersCorrection).join(', '),
  '\n  views/admin/orders-receipt-actions:', Object.keys(adminOrdersReceiptActions).join(', '),
  '\n  views/admin/orders-split:     ', Object.keys(adminOrdersSplit).join(', '),
  '\n  views/admin/orders-notify:    ', Object.keys(adminOrdersNotify).join(', '),
  '\n  views/admin/day-close:        ', Object.keys(adminDayClose).join(', '),
  '\n  views/admin/day-close-state:          ', Object.keys(adminDayCloseState).join(', '),
  '\n  views/admin/day-close-render:         ', Object.keys(adminDayCloseRender).join(', '),
  '\n  views/admin/day-close-zbon-action:    ', Object.keys(adminDayCloseZbonAction).join(', '),
  '\n  views/admin/day-close-archive-detail: ', Object.keys(adminDayCloseArchiveDetail).join(', '),
  '\n  views/admin/receipt:          ', Object.keys(adminReceipt).join(', '),
  '\n  utils/datev-export:           ', Object.keys(datevExport).join(', '),
  '\n  utils/dsfinvk-export:         ', Object.keys(dsfinvkExport).join(', '),
  '\n  views/admin/occupancy:        ', Object.keys(adminOccupancy).join(', '),
  '\n  views/admin/table-signals:    ', Object.keys(adminTableSignals).join(', '),
  '\n  views/admin/table-cart:       ', Object.keys(adminTableCart).join(', '),
  '\n  views/admin/layout-editor:    ', Object.keys(adminLayout).join(', '),
  '\n  views/admin/daily-sheet:      ', Object.keys(adminDailySheet).join(', '),
  '\n  views/admin/kds:              ', Object.keys(adminKds).join(', '),
  '\n  views/admin/bar:              ', Object.keys(adminBar).join(', '),
  '\n  views/admin/delivery:         ', Object.keys(adminDelivery).join(', '),
  '\n  views/driver-terminal:        ', Object.keys(driverTerminal).join(', '),
  '\n  views/admin/archive:          ', Object.keys(adminArchive).join(', '),
  '\n  views/admin/archive-state:    ', Object.keys(adminArchiveState).join(', '),
  '\n  views/admin/archive-helpers:  ', Object.keys(adminArchiveHelpers).join(', '),
  '\n  views/admin/archive-reservations:', Object.keys(adminArchiveReservations).join(', '),
  '\n  views/admin/archive-orders:   ', Object.keys(adminArchiveOrders).join(', '),
  '\n  views/admin/archive-bons:     ', Object.keys(adminArchiveBons).join(', '),
  '\n  views/admin/archive-zbons:    ', Object.keys(adminArchiveZbons).join(', '),
  '\n  views/admin/archive-mails:    ', Object.keys(adminArchiveMails).join(', '),
  '\n  views/admin/admin-order-entry:', Object.keys(adminOrderEntry).join(', '),
  '\n  views/admin/admin-order-entry-state:   ', Object.keys(adminOrderEntryState).join(', '),
  '\n  views/admin/admin-order-entry-delivery:', Object.keys(adminOrderEntryDelivery).join(', '),
  '\n  views/admin/admin-order-entry-dinein:  ', Object.keys(adminOrderEntryDinein).join(', '),
  '\n  views/admin/admin-order-entry-submit:  ', Object.keys(adminOrderEntrySubmit).join(', '),
  '\n  views/assignment-picker:      ', Object.keys(assignmentPicker).join(', '),
  '\n  views/walkin:                 ', Object.keys(walkin).join(', '),
  '\n  realtime:                     ', Object.keys(realtime).join(', '));
} // end if (import.meta.env.DEV)

// HMR-Hook
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    console.info('[main.js] HMR-Update angenommen.');
  });
}

// Booten — wir warten, bis Adapter (config.js, supabase-js, adapters/*.js)
// als deferred classic scripts ausgeführt wurden. DOMContentLoaded ist nach
// type=module ohnehin abgeschlossen; falls supabase-js noch nicht da ist,
// machen wir einen kurzen Wait-Loop.
async function waitForAdapters(maxMs = 3000) {
  const start = Date.now();
  while (!(window.App && window.App.storage && window.App.mailer && window.App.auth && window.App.events && window.supabase)) {
    if (Date.now() - start > maxMs) {
      console.error('[main.js] Adapters not ready after ' + maxMs + 'ms');
      return false;
    }
    await new Promise(r => setTimeout(r, 50));
  }
  return true;
}

(async () => {
  const ok = await waitForAdapters();
  if (!ok) {
    const t = document.getElementById('toast');
    if (t) {
      t.textContent = 'Initialisierung fehlgeschlagen — Adapter nicht verfügbar.';
      t.className = 'toast show error';
    }
    return;
  }
  try {
    await bootstrap();
  } catch (err) {
    console.error('Bootstrap-Fehler:', err);
    const t = document.getElementById('toast');
    if (t) {
      t.textContent = 'Initialisierung fehlgeschlagen — siehe Konsole (' + (err && err.message ? err.message : 'unbekannt') + ')';
      t.className = 'toast show error';
    }
  }
})();
